import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { LEGAL_CONTENT } from "../data/legal";

const TABS = [
  { key: "mentions-legales", label: "Mentions légales" },
  { key: "confidentialite", label: "Politique de confidentialité" },
  { key: "cgv", label: "CGV" },
];

export default function Legal() {
  const location = useLocation();
  const activeKey = location.hash.replace("#", "") || "mentions-legales";
  const active = TABS.find((t) => t.key === activeKey) || TABS[0];
  const content = LEGAL_CONTENT[active.key];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeKey]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 pb-24 pt-32 sm:pt-40" data-testid="legal-page">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-[#5B6B85] transition-colors hover:text-[#2563EB]"
          data-testid="legal-back-home"
        >
          <ArrowLeft size={16} /> Retour à l&apos;accueil
        </Link>

        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2563EB]">
          Informations légales
        </p>
        <h1 className="font-head mt-3 text-4xl font-extrabold text-[#0B1220] sm:text-5xl">
          {active.label}
        </h1>

        <nav className="mt-8 flex flex-wrap gap-2" data-testid="legal-tabs">
          {TABS.map((t) => (
            <Link
              key={t.key}
              to={`/mentions-legales#${t.key}`}
              data-testid={`legal-tab-${t.key}`}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                t.key === active.key
                  ? "border-[#2563EB] bg-[#2563EB] text-white shadow-[0_10px_30px_-16px_rgba(37,99,235,0.6)]"
                  : "border-[#E6EDF6] bg-white text-[#0B1220] hover:border-[#2563EB]/40"
              }`}
            >
              {t.label}
            </Link>
          ))}
        </nav>

        <motion.article
          key={active.key}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          data-testid="legal-content"
          className="mt-10 space-y-8 rounded-3xl border border-[#E6EDF6] bg-white p-6 shadow-[0_10px_40px_-20px_rgba(15,23,42,0.2)] sm:p-10"
        >
          {content.map((block, i) => (
            <LegalBlock key={i} block={block} />
          ))}
        </motion.article>

        <p className="mt-8 text-xs text-[#94A3B8]">
          Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
        </p>
      </main>
      <Footer />
    </div>
  );
}

function LegalBlock({ block }) {
  if (block.h === 2) {
    return (
      <h2 className="font-head text-2xl font-bold text-[#0B1220] sm:text-3xl">
        {block.text}
      </h2>
    );
  }
  if (block.h === 3) {
    return (
      <h3 className="font-head text-lg font-bold text-[#0B1220] sm:text-xl">
        {block.text}
      </h3>
    );
  }
  if (block.list) {
    return (
      <ul className="ml-5 list-disc space-y-2 text-sm leading-relaxed text-[#334155] sm:text-base">
        {block.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  return (
    <p className="whitespace-pre-line text-sm leading-relaxed text-[#334155] sm:text-base">
      {block.text}
    </p>
  );
}
